<template>
  <div class="filter-panel-wrapper">
    <v-expand-transition>
      <div
        v-if="show"
        class="filter-panel"
        data-product-fruits="ad-materials-filter-panel"
      >
        <div class="filter-header">
          <h3>{{ $t('ad-materials.filter.title') }}</h3>
          <v-btn
            icon
            variant="text"
            @click="closePanel"
            density="comfortable"
          >
            <v-icon class="text-grey-darken-2">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="filter-content">
          <!-- Target Audience -->
          <div class="filter-section">
            <div
              class="section-header"
              @click="toggleSection('target')"
            >
              <h4>{{ $t('ad-materials.filter.target.label') }}</h4>
              <v-icon>{{ expandedSections.target ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="expandedSections.target">
                <div class="checkbox-group">
                  <v-radio-group v-model="selectedTargets">
                    <v-radio
                      v-for="(label, value) in targetOptions"
                      :key="value"
                      color="primary"
                      :label="label"
                      :value="value"
                      density="compact"
                    />
                  </v-radio-group>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <!-- Topic -->
          <div
            class="filter-section"
            v-if="topics.length"
          >
            <div
              class="section-header"
              @click="toggleSection('topic')"
            >
              <h4>{{ $t('ad-materials.filter.topic.label') }}</h4>
              <v-icon>{{ expandedSections.topic ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <v-expand-transition>
              <div
                v-show="expandedSections.topic"
                class="checkbox-group"
              >
                <v-checkbox
                  v-for="topic in topics"
                  :key="topic.id"
                  v-model="selectedTopics"
                  color="primary"
                  :value="topic.id"
                  density="compact"
                >
                  <template v-slot:label>
                    <span class="filter-label">
                      {{ topic.name }}
                      <span
                        v-if="topic.count !== undefined"
                        class="count-badge"
                        >({{ topic.count }})</span
                      >
                    </span>
                  </template>
                </v-checkbox>
              </div>
            </v-expand-transition>
          </div>

          <!-- Asset Type -->
          <div class="filter-section">
            <div
              class="section-header"
              @click="toggleSection('assetType')"
            >
              <h4>{{ $t('ad-materials.filter.asset-type.label') }}</h4>
              <v-icon>{{
                expandedSections.assetType ? 'mdi-chevron-up' : 'mdi-chevron-down'
              }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="expandedSections.assetType">
                <v-checkbox
                  v-for="assetType in props.assetTypes"
                  v-model="selectedAssetTypes"
                  :key="assetType.type"
                  color="primary"
                  :value="assetType.type"
                  density="compact"
                >
                  <template v-slot:label>
                    <span class="filter-label">
                      {{ getAssetTypeLabel(assetType.type) }}
                      <span
                        v-if="assetType.count !== undefined"
                        class="count-badge"
                        >({{ assetType.count }})</span
                      >
                    </span>
                  </template>
                </v-checkbox>
              </div>
            </v-expand-transition>
          </div>

          <!-- Origin -->
          <div class="filter-section">
            <div
              class="section-header"
              @click="toggleSection('origin')"
            >
              <h4>{{ $t('ad-materials.filter.origin.label') }}</h4>
              <v-icon>{{ expandedSections.origin ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="expandedSections.origin">
                <v-checkbox
                  v-for="imageSource in props.imageSources"
                  v-model="selectedOrigins"
                  :key="imageSource.source"
                  color="primary"
                  :value="imageSource.source"
                  density="compact"
                >
                  <template v-slot:label>
                    <span class="filter-label">
                      {{ getOriginTypeLabel(imageSource.source) }}
                      <span
                        v-if="imageSource.count !== undefined"
                        class="count-badge"
                        >({{ imageSource.count }})</span
                      >
                    </span>
                  </template>
                </v-checkbox>
              </div>
            </v-expand-transition>
          </div>
        </div>

        <div class="filter-footer">
          <p class="info-text">{{ $t('ad-materials.filter.footer') }}</p>
          <v-btn
            block
            color="primary"
            variant="outlined"
            @click="resetFilters"
          >
            {{ $t('ad-materials.filter.reset') }}
          </v-btn>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useBranding } from '@/composables/useBranding'

const { t } = useI18n()

interface Filters {
  target: 'all' | 'buyers' | 'sellers'
  topics: string[]
  origins: string[]
  search: string
  sort: string
  assetTypes: []
}

interface Topic {
  id: string | number
  name: string
  count?: number
}

interface AssetTypeFilter {
  type: string
  count: number
}

interface ImageSourceFilter {
  source: string
  count: number
}

const props = withDefaults(
  defineProps<{
    show: boolean
    topics: Topic[]
    activeFilters: Filters
    assetTypes?: AssetTypeFilter[]
    imageSources?: ImageSourceFilter[]
  }>(),
  {
    show: false,
    topics: () => [],
    assetTypes: () => [],
    imageSources: () => [],
  }
)

const emit = defineEmits(['update:show', 'filter-change'])

const selectedTargets = ref<Filters['target']>('all')
const selectedTopics = ref<Filters['topics']>([])
const selectedOrigins = ref<Filters['origins']>([])
const selectedAssetTypes = ref<Filters['assetTypes']>([])

const expandedSections = ref({
  target: true,
  topic: true,
  origin: true,
  assetType: true,
})

const targetOptions = {
  buyers: t('ad-materials.filter.target.options.buyers'),
  sellers: t('ad-materials.filter.target.options.sellers'),
  all: t('ad-materials.filter.target.options.show-all'),
}

const getAssetTypeLabel = (type: string): string => {
  const assetTypeLabels: Record<string, string> = {
    SHOPPING_CART: t('ad-materials.filter.asset-type.options.shopping_cart'),
    CHECKLIST: t('ad-materials.filter.asset-type.options.checklist'),
    UNKNOWN: t('ad-materials.filter.asset-type.options.unknown'),
    AD: t('ad-materials.filter.asset-type.options.ad'),
    FLYER_A5: t('ad-materials.filter.asset-type.options.flyer_a5'),
    FLYER: t('ad-materials.filter.asset-type.options.flyer'),
    POSTER: t('ad-materials.filter.asset-type.options.poster'),
    BANNER: t('ad-materials.filter.asset-type.options.banner'),
    SOCIAL_IMAGE: t('ad-materials.filter.asset-type.options.social_image'),
    SOCIAL_STORY: t('ad-materials.filter.asset-type.options.social_story'),
    COASTER: t('ad-materials.filter.asset-type.options.coaster'),
    POSTCARD_BLANK: t('ad-materials.filter.asset-type.options.postcard_blank'),
    POSTCARD_TEXT: t('ad-materials.filter.asset-type.options.postcard_text'),
    NEWSPAPER: t('ad-materials.filter.asset-type.options.newspaper'),
    GUIDE: t('ad-materials.filter.asset-type.options.guide'),
    VIDEO: t('ad-materials.filter.asset-type.options.video'),
  }
  return assetTypeLabels[type] || type
}

const getOriginTypeLabel = (source: string): string => {
  return t(`content-creator.image-sources.${source}`, {
    platform: useBranding().readablePlatformName.value,
  })
}

const toggleSection = (section: keyof typeof expandedSections.value) => {
  expandedSections.value[section] = !expandedSections.value[section]
}

const closePanel = () => {
  emit('update:show', false)
}

const resetFilters = () => {
  selectedTargets.value = 'all'
  selectedTopics.value = []
  selectedOrigins.value = []
  selectedAssetTypes.value = []
  emitFilterChange()
}

const emitFilterChange = () => {
  emit('filter-change', {
    target: selectedTargets.value,
    topics: selectedTopics.value,
    origins: selectedOrigins.value,
    assetTypes: selectedAssetTypes.value,
  })
}

const initFilters = () => {
  selectedTargets.value = props.activeFilters?.target || 'all'
  selectedTopics.value = props.activeFilters?.topics || []
  selectedOrigins.value = props.activeFilters?.origins || []
  selectedAssetTypes.value = props.activeFilters?.assetTypes || []
}

onMounted(() => {
  initFilters()
})

watch([selectedTargets, selectedTopics, selectedOrigins, selectedAssetTypes], () => {
  emitFilterChange()
})
</script>

<style scoped>
.filter-panel-wrapper {
  width: 100%;
  max-width: 300px;
  flex-shrink: 0;
}

.filter-panel {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 1px 0 8px rgba(0, 0, 0, 0.1);
  height: calc(100% - 170px);
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-right: 0;
  overflow: hidden;
}

.filter-header {
  padding: 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 16px 16px 16px;
  margin-right: -17px;
  padding-right: calc(16px + 17px);
}

.filter-section {
  margin-bottom: 16px;
  border: 1px solid rgb(var(--v-theme-on-surface-variant));
  border-radius: 8px;
  padding: 4px 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
}

.section-header h4 {
  margin: 0;
  font-weight: 500;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
}

.filter-footer {
  padding: 16px;
  border-top: 1px solid #eee;
}

.info-text {
  font-size: 12px;
  color: #666;
  margin-bottom: 16px;
}

@media (max-width: 600px) {
  .filter-panel-wrapper {
    width: 100%;
  }
}

.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}

.v-expand-transition-enter-from,
.v-expand-transition-leave-to {
  opacity: 0;
  width: 0;
}

:deep(.v-input__details) {
  display: none !important;
}

.filter-label {
  display: inline-flex;
  align-items: center;
  width: 100%;
  flex-wrap: nowrap;
}

.count-badge {
  color: #666;
  font-size: 0.875rem;
  font-weight: normal;
  margin-left: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}
</style>
